import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useBlocker,
  type BlockerFunction,
} from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ReactFlow,
  BaseEdge,
  Background,
  Controls,
  EdgeLabelRenderer,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  getBezierPath,
  type Node,
  type Edge,
  type Connection,
  type EdgeProps,
  type EdgeTypes,
  type NodeTypes,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { ArrowLeft, Check, GitBranch, Loader2, Play, History, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/tenant-context";
import { workflowsApi, workflowRunsApi } from "@/api/workflows";
import { ApiError } from "@/api/client";
import { queryKeys } from "@/lib/query-keys";
import type { WorkflowNodeType, WorkflowRunColumn, WorkflowStatus } from "@proclaw/shared";
import { rewriteColumnSources } from "@/lib/workflow-run-columns";
import { TriggerNode } from "@/components/workflows/nodes/trigger-node";
import { AgentNode } from "@/components/workflows/nodes/agent-node";
import { ConditionNode } from "@/components/workflows/nodes/condition-node";
import { ActionNode } from "@/components/workflows/nodes/action-node";
import { NodePalette } from "@/components/workflows/node-palette";
import { ConfigPanel } from "@/components/workflows/config-panel";
import { NoticeDialog, type WorkflowNotice } from "@/components/workflows/notice-dialog";
import { VersionHistoryDialog } from "@/components/workflows/version-history-dialog";
import { withWorkspaceContext } from "@/lib/workspace-navigation";
import { cn } from "@/lib/utils";

const MOBILE_BREAKPOINT = 800;

const nodeTypes: NodeTypes = {
  trigger: TriggerNode,
  agent: AgentNode,
  condition: ConditionNode,
  action: ActionNode,
};

function DeletableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerStart,
  markerEnd,
  style,
  interactionWidth,
  deletable,
}: EdgeProps) {
  const { deleteElements } = useReactFlow();
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerStart={markerStart}
        markerEnd={markerEnd}
        style={style}
        interactionWidth={interactionWidth}
      />
      {deletable && (
        <EdgeLabelRenderer>
          <button
            type="button"
            className="nodrag nopan flex h-6 w-6 items-center justify-center rounded-full border border-border bg-surface text-text-muted shadow-sm transition-colors hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            style={{
              pointerEvents: "all",
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            }}
            onClick={(event) => {
              event.stopPropagation();
              void deleteElements({ edges: [{ id }] });
            }}
            aria-label="Delete connection"
            title="Delete connection"
          >
            <Trash2 size={11} />
          </button>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const edgeTypes: EdgeTypes = {
  deletable: DeletableEdge,
};

/**
 * Seed data for newly-dropped nodes. Keeps the canvas from looking empty
 * before the user fills in the config rail.
 */
function defaultNodeData(type: WorkflowNodeType): Record<string, unknown> {
  switch (type) {
    case "trigger":
      return { subtype: "manual", label: "Trigger" };
    case "agent":
      return { label: "Agent" };
    case "condition":
      return { label: "Condition", mode: "binary", expression: "" };
    case "action":
      return {
        actionType: "http_request",
        label: "Action",
        config: { method: "POST", body: "" },
      };
  }
}

/**
 * Turn a node title into a URL-safe id slug: lowercase, non-alphanumeric runs
 * collapse to a single hyphen, ends trimmed. An empty title falls back to the
 * node type so an id is always produced ("Send Welcome Email" → send-welcome-email).
 */
function slugifyLabel(label: unknown, fallbackType: string): string {
  const slug = (typeof label === "string" ? label : "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || fallbackType;
}

/**
 * First free id of the form `base`, `base-2`, `base-3`… given the ids already
 * taken by other nodes. Two nodes titled the same stay unique without
 * surprising the user (two "Send Email" nodes → send-email, send-email-2).
 */
function uniqueNodeId(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/**
 * Bring each node's LABEL back in line with its id. When a node is renamed to a
 * title that already exists, its id is de-duplicated immediately (e.g.
 * `normalize-lead-2`) but the label is left exactly as typed ("Normalize
 * Lead"), so the two diverge. Applied on save: if the user never fixed the
 * clash, this appends the id's numeric suffix to the label ("Normalize Lead
 * 2") so the most-recently-edited node ends up numbered and its label matches
 * its id. Only the label text changes — ids, edges and {{steps.<id>…}}
 * references already point at the deduped id, so nothing cascades. Idempotent.
 */
function syncLabelsToIds(nodes: Node[]): {
  nodes: Node[];
  renamed: Array<{ from: string; to: string }>;
} {
  const renamed: Array<{ from: string; to: string }> = [];
  const next = nodes.map((n) => {
    const data = (n.data ?? {}) as Record<string, unknown>;
    const label = data.label;
    if (typeof label !== "string" || !label.trim()) return n;
    const base = slugifyLabel(label, (n.type as string) ?? "node");
    if (n.id === base || !n.id.startsWith(`${base}-`)) return n;
    const suffix = n.id.slice(base.length + 1);
    if (!/^\d+$/.test(suffix)) return n;
    const to = `${label} ${suffix}`;
    renamed.push({ from: label, to });
    return { ...n, data: { ...data, label: to } };
  });
  return { nodes: next, renamed };
}

/**
 * Rewrite every `{{steps.<oldId>.…}}` variable reference to point at <newId>,
 * walking strings, arrays and objects. The trailing dot anchors the match to a
 * whole id, so renaming `agent` never touches `agent-2`'s references.
 */
function rewriteStepRefs(value: unknown, oldId: string, newId: string): unknown {
  if (typeof value === "string") {
    return value.split(`steps.${oldId}.`).join(`steps.${newId}.`);
  }
  if (Array.isArray(value)) {
    return value.map((v) => rewriteStepRefs(v, oldId, newId));
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = rewriteStepRefs(v, oldId, newId);
    }
    return out;
  }
  return value;
}

/**
 * WorkflowCanvas — ReactFlow-based editor for a single workflow.
 *
 * Layout: left rail (palette, admin-only) → centre (canvas) → right rail
 * (config panel for selected node). On narrow viewports the canvas is
 * swapped for a copy prompt since ReactFlow doesn't work well on phones.
 */
export function WorkflowCanvas() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const [viewportWide, setViewportWide] = useState(
    typeof window === "undefined" ? true : window.innerWidth >= MOBILE_BREAKPOINT,
  );
  useEffect(() => {
    const onResize = () => setViewportWide(window.innerWidth >= MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!workflowId) return null;
  if (!viewportWide) return <NarrowViewportFallback />;

  return (
    <ReactFlowProvider>
      <WorkflowCanvasInner workflowId={workflowId} />
    </ReactFlowProvider>
  );
}

function WorkflowCanvasInner({ workflowId }: { workflowId: string }) {
  // A workflow is agent configuration, so agent admins own it end to end:
  // editing, saving, running, and switching it between draft/active/paused.
  // This matches the server, whose workflow routes all require agent_admin.
  const { tenantId, canManageAnyAgent: canEdit } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The agent we came from (if any). Carried in the URL so "back" returns to
  // that agent's Workflows view instead of dropping to the tenant-wide list.
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get("agentId");
  // Built by the shared helper so the list scope (wfScope) rides back along
  // with the agent and chat context. Hand-rolling the params here dropped it,
  // which sent the user to "This agent" even when they came from "All
  // workflows".
  const workflowsBackPath = withWorkspaceContext(
    `/tenants/${tenantId}/workspace?mode=workflows`,
    searchParams,
  );

  const { data: workflow, isLoading } = useQuery({
    queryKey: queryKeys.workflows.detail(workflowId),
    queryFn: () => workflowsApi.get(workflowId),
    enabled: !!workflowId,
  });

  // Canvas state — seeded from the query result once loaded.
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [status, setStatus] = useState<WorkflowStatus>("draft");
  const [dirty, setDirty] = useState(false);
  // Run-table columns ride along with canvas saves because their source
  // paths (`steps.<nodeId>.outputs.<key>`) reference node ids, and node ids
  // are derived from node titles — so a rename silently orphaned every column
  // pointing at that node and the runs table just started showing blanks.
  const [runColumns, setRunColumns] = useState<WorkflowRunColumn[]>([]);
  const runColumnsRef = useRef<WorkflowRunColumn[]>([]);
  runColumnsRef.current = runColumns;
  // Canvas-level message shown in a centred dialog rather than window.alert,
  // which the browser draws at the top of the window with the page URL and
  // cannot be styled to look like the product.
  const [notice, setNotice] = useState<WorkflowNotice | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition, fitView } = useReactFlow();
  const canvasEdges = useMemo(
    () =>
      edges.map((edge) => ({
        ...edge,
        type: "deletable",
        deletable: canEdit,
      })),
    [edges, canEdit],
  );

  // Seed editor state from the server data once, on first load.
  //
  // baseUpdatedAtRef tracks the version this editor is working from: seeded
  // here, advanced on every successful save. It goes up with each save so a
  // second save isn't rejected by its own first one, and a change made
  // elsewhere (the canvas in another tab, or an agent editing from chat) is
  // caught instead of being silently overwritten.
  const seededRef = useRef(false);
  const baseUpdatedAtRef = useRef<string | undefined>(undefined);
  // Nodes whose label had to be numbered on the last save, so the result can
  // say so. Renaming what someone typed without telling them reads as "my
  // change didn't save".
  const pendingRenamesRef = useRef<Array<{ from: string; to: string }>>([]);
  useEffect(() => {
    if (!workflow || seededRef.current) return;
    setNodes((workflow.nodes ?? []) as unknown as Node[]);
    setEdges((workflow.edges ?? []) as unknown as Edge[]);
    setName(workflow.name);
    setStatus(workflow.status);
    setRunColumns(workflow.runColumns ?? []);
    baseUpdatedAtRef.current = workflow.updatedAt;
    seededRef.current = true;
  }, [workflow, setNodes, setEdges]);

  // On first load, frame the trigger node rather than the whole graph, so the
  // user lands where the flow starts and can head straight into it. Runs once,
  // after nodes are seeded; the nested rAF lets React Flow measure the newly
  // set nodes before we fit. Falls back to fitting everything if there is no
  // trigger yet (e.g. a brand-new empty workflow).
  const framedRef = useRef(false);
  useEffect(() => {
    if (framedRef.current || nodes.length === 0) return;
    framedRef.current = true;
    const trigger = nodes.find((n) => n.type === "trigger");
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (trigger) {
          fitView({
            nodes: [{ id: trigger.id }],
            padding: 0.6,
            maxZoom: 1,
            duration: 300,
          });
        } else {
          fitView({ padding: 0.25, duration: 300 });
        }
      }),
    );
  }, [nodes, fitView]);

  // Track dirty state for the Save button. We flip dirty=true on any
  // user-initiated change; seed-effects don't (the `seededRef` guards them).
  const markDirty = useCallback(() => setDirty(true), []);

  // Leaving mid-edit used to bin the work in silence, and a canvas edit is
  // expensive: layout, prompts, wiring.
  //
  // Closing or reloading the tab is the browser's own prompt. Everything
  // inside the app is useBlocker, which sits below react-router itself and so
  // catches every route change however it was started: the back arrow, the
  // workspace top-bar tabs, the sidebar chat list, sidebar links, and the
  // browser's back button. Guarding each of those by hand missed the ones
  // that call navigate() directly, which is most of them.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // Browsers show their own wording; setting returnValue is what actually
      // arms the prompt, the value is ignored.
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        dirty && currentLocation.pathname !== nextLocation.pathname,
      [dirty],
    ),
  );

  // A blocked navigation raises the same dialog the back arrow used to raise
  // on its own. proceed() replays the destination the user actually clicked,
  // so they land where they meant to rather than back on the workflow list.
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    setNotice({
      tone: "info",
      title: "You have unsaved changes",
      lines: [
        "Leaving now discards everything since your last save, including node positions, prompts and wiring.",
      ],
      confirm: {
        label: "Leave without saving",
        cancelLabel: "Stay",
        destructive: true,
        onConfirm: () => {
          setNotice(null);
          blocker.proceed?.();
        },
      },
    });
  }, [blocker]);

  // Dismissing the dialog any other way (Stay, Escape, backdrop) has to
  // release the blocker too, or the next attempt to leave is ignored because
  // react-router still thinks this one is unresolved.
  const dismissNotice = useCallback(() => {
    setNotice(null);
    if (blocker.state === "blocked") blocker.reset?.();
  }, [blocker]);

  // ── Undo history ──────────────────────────────────────────────────────
  // ReactFlow has no built-in undo. We snapshot (nodes, edges) BEFORE each
  // structural change (delete, add, connect, drag) so Ctrl/Cmd+Z restores the
  // previous graph — e.g. bringing back a node the user just deleted. Snapshots
  // store the current array references; ReactFlow updates immutably, so the
  // stored arrays are never mutated and don't need deep-cloning.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;
  const historyRef = useRef<Array<{ nodes: Node[]; edges: Edge[] }>>([]);
  const pushHistory = useCallback(() => {
    historyRef.current.push({ nodes: nodesRef.current, edges: edgesRef.current });
    if (historyRef.current.length > 100) historyRef.current.shift();
  }, []);
  const undo = useCallback(() => {
    const prev = historyRef.current.pop();
    if (!prev) return;
    setNodes(prev.nodes);
    setEdges(prev.edges);
    setSelectedNodeId(null);
    markDirty();
  }, [setNodes, setEdges, markDirty]);

  // Ctrl/Cmd+Z anywhere on the canvas → undo. Ignored while typing in a form
  // field (inputs keep their native text undo) and for viewers.
  useEffect(() => {
    if (!canEdit) return;
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod || e.shiftKey || e.key.toLowerCase() !== "z") return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
      e.preventDefault();
      undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit, undo]);

  const save = useMutation({
    mutationFn: (override?: { nodes: Node[]; edges: Edge[] }) =>
      workflowsApi.update(workflowId, {
        name,
        status,
        nodes: (override?.nodes ?? nodes) as never,
        edges: (override?.edges ?? edges) as never,
        expectedUpdatedAt: baseUpdatedAtRef.current,
        // Sent so a node rename carries through to the columns that source
        // from it. Read from the ref rather than the closure: renames and the
        // save can land in the same tick, and the stale closure value would
        // write the pre-rename sources straight back over the fix.
        runColumns: runColumnsRef.current,
      }),
    onSuccess: (row) => {
      setDirty(false);
      baseUpdatedAtRef.current = row.updatedAt;
      // A node name that clashes with another gets numbered so the visible name
      // matches the id that {{steps.<id>...}} references point at. Say which,
      // rather than leaving the user to spot it.
      const renamed = pendingRenamesRef.current;
      pendingRenamesRef.current = [];
      if (renamed.length > 0) {
        setNotice({
          tone: "info",
          title: "Saved, with renamed nodes",
          lines: [
            "These names clashed with a node that already had them, so they were numbered to keep each name matching its step id.",
          ],
          details: renamed.map((r) => `"${r.from}"  \u2192  "${r.to}"`),
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(workflowId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.byCompany(tenantId) });
      // Adding/removing an Agent node changes the agent-scoped list too, so
      // refresh it — otherwise "This agent" shows a stale set after a save.
      if (agentId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.workflowsByAgent(agentId) });
      }
    },
    onError: async (err: unknown) => {
      const message = err instanceof Error ? err.message : "Unknown error";
      // A 409 means someone else saved while this editor was open, so the only
      // honest recovery is to re-seed from the server. Anything else (a
      // validation rejection, a network failure) leaves the user's edits in
      // place so they can fix the problem and press Save again.
      if (err instanceof ApiError && err.status === 409) {
        setNotice({
          tone: "error",
          title: "This workflow changed elsewhere",
          lines: [message],
        });
        seededRef.current = false;
        await queryClient.invalidateQueries({
          queryKey: queryKeys.workflows.detail(workflowId),
        });
        setDirty(false);
        return;
      }
      // Never fail silently: without this a rejected save looked identical to
      // a save that had simply done nothing.
      setNotice({ tone: "error", title: "Could not save workflow", lines: [message] });
    },
  });

  // Save button: first bring any diverged labels back in line with their ids
  // (a duplicate rename the user never fixed → the recent node becomes "… 2"),
  // then persist. The synced graph is passed explicitly because setNodes is
  // async and the mutation would otherwise read the pre-sync state.
  const handleSave = useCallback(() => {
    const { nodes: synced, renamed } = syncLabelsToIds(nodesRef.current);
    pendingRenamesRef.current = renamed;
    if (renamed.length > 0) {
      pushHistory();
      setNodes(synced);
      save.mutate({ nodes: synced, edges: edgesRef.current });
    } else {
      save.mutate(undefined);
    }
  }, [save, setNodes, pushHistory]);

  // ── Run kick-off ──────────────────────────────────────────────────────
  // For v1 we just POST with empty inputs and route to the run timeline.
  // A future iteration can show an inputs modal when the trigger has a
  // declared input schema. Save first if dirty so the running graph
  // matches what the user sees on screen.
  const runWorkflow = useMutation({
    mutationFn: async () => {
      if (dirty) {
        await save.mutateAsync(undefined);
      }
      return workflowRunsApi.start(workflowId, {});
    },
    onSuccess: (run) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflowRuns.byWorkflow(workflowId),
      });
      navigate(
        withWorkspaceContext(
          `/tenants/${tenantId}/workflows/${workflowId}/runs/${run.id}`,
          searchParams,
        ),
      );
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Unknown error";
      setNotice({ tone: "error", title: "Could not start the run", lines: [message] });
    },
  });

  // ── ReactFlow callbacks ───────────────────────────────────────────────

  const onConnect = useCallback(
    (params: Edge | Connection) => {
      pushHistory();
      setEdges((eds) => addEdge({ ...params, animated: true }, eds));
      markDirty();
    },
    [setEdges, markDirty, pushHistory],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/reactflow") as WorkflowNodeType;
      if (!type) return;
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const data = defaultNodeData(type);
      const taken = new Set(nodesRef.current.map((n) => n.id));
      const base = slugifyLabel(data.label, type);
      const id = uniqueNodeId(base, taken);
      // When the id had to be de-duplicated (trigger -> trigger-2), mirror the
      // number in the visible label so the card reads "Trigger 2" and the title
      // stays in sync with its id. Without this, a second dropped node looks
      // identical to the first even though its id already differs.
      if (id !== base && typeof data.label === "string" && data.label) {
        data.label = `${data.label} ${id.slice(base.length + 1)}`;
      }
      pushHistory();
      setNodes((nds) => [
        ...nds,
        { id, type, position, data } as Node,
      ]);
      setSelectedNodeId(id);
      markDirty();
    },
    [screenToFlowPosition, setNodes, markDirty, pushHistory],
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
  }, []);

  const onPaneClick = useCallback(() => setSelectedNodeId(null), []);

  // ── Config panel wire-up ──────────────────────────────────────────────

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId],
  );

  const updateSelectedNodeData = useCallback(
    (data: Record<string, unknown>) => {
      if (!selectedNodeId) return;
      const selected = nodesRef.current.find((node) => node.id === selectedNodeId);
      if (selected?.type === "condition") {
        const allowedHandles =
          data.mode === "multiple" && Array.isArray(data.choices)
            ? new Set(
                (data.choices as Array<{ id?: unknown }>)
                  .map((choice) => choice.id)
                  .filter((id): id is string => typeof id === "string"),
              )
            : new Set(["yes", "no"]);
        const removesConnection = edgesRef.current.some(
          (edge) =>
            edge.source === selectedNodeId &&
            edge.sourceHandle != null &&
            !allowedHandles.has(edge.sourceHandle),
        );
        if (removesConnection) {
          pushHistory();
          setEdges((currentEdges) =>
            currentEdges.filter(
              (edge) =>
                edge.source !== selectedNodeId ||
                edge.sourceHandle == null ||
                allowedHandles.has(edge.sourceHandle),
            ),
          );
        }
      }
      if (
        selected?.type === "action" &&
        selected.data.actionType === "wait_for_email_reply" &&
        data.actionType !== "wait_for_email_reply"
      ) {
        const removesConnection = edgesRef.current.some(
          (edge) => edge.source === selectedNodeId,
        );
        if (removesConnection) {
          pushHistory();
          setEdges((currentEdges) =>
            currentEdges.filter((edge) => edge.source !== selectedNodeId),
          );
        }
      }
      // Derive the node id from its title so ids stay human-readable, and let
      // the edges + {{steps.<id>…}} variables that point at this node follow
      // the rename. Only recompute when the title actually changed, so editing
      // an unrelated field never quietly shuffles the id.
      const labelChanged =
        typeof data.label === "string" &&
        data.label !==
          (selected?.data as { label?: unknown } | undefined)?.label;
      const taken = new Set(
        nodesRef.current
          .filter((n) => n.id !== selectedNodeId)
          .map((n) => n.id),
      );
      const newId = labelChanged
        ? uniqueNodeId(slugifyLabel(data.label, selected?.type ?? "node"), taken)
        : selectedNodeId;

      setNodes((nds) => {
        const withData = nds.map((n) =>
          n.id === selectedNodeId ? { ...n, id: newId, data, selected: true } : n,
        );
        if (newId === selectedNodeId) return withData;
        return withData.map((n) => ({
          ...n,
          data: rewriteStepRefs(n.data, selectedNodeId, newId) as Record<
            string,
            unknown
          >,
        }));
      });
      if (newId !== selectedNodeId) {
        setEdges((eds) =>
          eds.map((e) => ({
            ...e,
            source: e.source === selectedNodeId ? newId : e.source,
            target: e.target === selectedNodeId ? newId : e.target,
          })),
        );
        // Run-table columns reference node ids too, in a separate jsonb column
        // the canvas doesn't otherwise touch. Without this they silently
        // orphan on rename.
        setRunColumns((cols) => rewriteColumnSources(cols, selectedNodeId, newId));
        setSelectedNodeId(newId);
      }
      markDirty();
    },
    [selectedNodeId, setNodes, setEdges, setSelectedNodeId, markDirty, pushHistory],
  );

  const deleteSelectedNode = useCallback(() => {
    if (!selectedNodeId) return;
    pushHistory();
    setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
    setEdges((eds) =>
      eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId),
    );
    setSelectedNodeId(null);
    markDirty();
  }, [selectedNodeId, setNodes, setEdges, markDirty, pushHistory]);

  if (isLoading || !workflow) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 size={18} className="animate-spin text-text-muted" />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border bg-surface px-4 py-2">
        <button
          onClick={() => navigate(workflowsBackPath)}
          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
          aria-label="Back to workflows"
        >
          <ArrowLeft size={14} />
        </button>

        <input
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            markDirty();
          }}
          readOnly={!canEdit}
          className={cn(
            "flex-1 rounded-md bg-transparent px-2 py-1 text-sm font-semibold text-text-primary outline-none transition-colors",
            canEdit && "hover:bg-surface-hover focus:bg-background focus:ring-1 focus:ring-accent",
          )}
        />

        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as WorkflowStatus);
            markDirty();
          }}
          disabled={!canEdit}
          className="rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-text-primary outline-none focus:border-accent disabled:cursor-not-allowed disabled:opacity-60"
        >
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
        </select>

        {/* Runs history — always visible (non-admins see read-only). */}
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            navigate(
              withWorkspaceContext(
                `/tenants/${tenantId}/workflows/${workflowId}/runs`,
                searchParams,
              ),
            )
          }
          className="gap-1.5"
        >
          <History size={12} />
          Runs
        </Button>

        {/* Version history — read-level, so non-admins can see who changed
            what even though only editors can restore. */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowVersions(true)}
          className="gap-1.5"
        >
          <GitBranch size={12} />
          History
        </Button>

        {canEdit && (
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={runWorkflow.isPending || save.isPending}
              onClick={() => runWorkflow.mutate()}
              className="gap-1.5"
            >
              {runWorkflow.isPending ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Play size={12} />
              )}
              Run
            </Button>
            <Button
              size="sm"
              data-tour="workflow-save"
              disabled={!dirty || save.isPending}
              onClick={handleSave}
              className="gap-1.5"
            >
              {save.isPending ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Check size={12} />
              )}
              {save.isPending ? "Saving…" : dirty ? "Save" : "Saved"}
            </Button>
          </>
        )}
      </div>

      {/* ── Main 3-pane layout ──────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1">
        {canEdit && <NodePalette />}

        <div
          ref={wrapperRef}
          className="relative flex-1"
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          <ReactFlow
            nodes={nodes}
            edges={canvasEdges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={(changes) => {
              // Snapshot before a keyboard/selection delete so Ctrl+Z can
              // bring the node(s) back. Position/select changes aren't
              // snapshotted here (drag is captured on drag-start instead).
              if (changes.some((c) => c.type === "remove")) pushHistory();
              onNodesChange(changes);
              // Position changes from user dragging mark dirty
              if (changes.some((c) => c.type === "position" || c.type === "remove")) {
                markDirty();
              }
            }}
            onEdgesChange={(changes) => {
              if (changes.some((c) => c.type === "remove")) pushHistory();
              onEdgesChange(changes);
              if (changes.some((c) => c.type === "remove")) markDirty();
            }}
            onNodeDragStart={(_, node) => {
              setSelectedNodeId(node.id);
              pushHistory();
            }}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeClickDistance={5}
            // Snap zone: dropping a connection within this many flow-units of a
            // node's handle latches on, so you don't have to land on the small
            // dot — makes wiring easy even zoomed out. (~¼ of a node's width.)
            connectionRadius={70}
            nodesDraggable={canEdit}
            nodesConnectable={canEdit}
            deleteKeyCode={canEdit ? ["Backspace", "Delete"] : null}
            elementsSelectable
            defaultEdgeOptions={{ animated: true }}
          >
            <Background gap={16} size={1} />
            <Controls showInteractive={false} />
            <MiniMap
              nodeStrokeWidth={3}
              pannable
              zoomable
              className="!bg-surface !rounded-md !border !border-border"
            />
          </ReactFlow>
        </div>

        <ConfigPanel
          node={selectedNode}
          nodes={nodes}
          edges={edges}
          onChange={updateSelectedNodeData}
          onDelete={deleteSelectedNode}
          readOnly={!canEdit}
          workflowId={workflowId}
        />
      </div>

      {notice && <NoticeDialog notice={notice} onClose={dismissNotice} />}
      {showVersions && workflowId && (
        <VersionHistoryDialog
          workflowId={workflowId}
          onClose={() => setShowVersions(false)}
          onRestored={() => {
            // The editor seeds its local nodes/edges from the server exactly
            // once. A restore changes the server copy underneath it, so clear
            // the seed latch to let the refetched graph re-seed the canvas —
            // otherwise the restore would appear to do nothing until reload.
            seededRef.current = false;
            setDirty(false);
          }}
        />
      )}
    </div>
  );
}

function NarrowViewportFallback() {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const [searchParams] = useSearchParams();
  const backPath = withWorkspaceContext(
    `/tenants/${tenantId}/workspace?mode=workflows`,
    searchParams,
  );
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-sm font-medium text-text-primary">
        Canvas editing requires a wider screen
      </p>
      <p className="max-w-sm text-xs text-text-muted">
        The workflow canvas is designed for desktop and tablet. Switch to a
        larger screen to edit this workflow.
      </p>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => navigate(backPath)}
      >
        Back to workflows
      </Button>
    </div>
  );
}
